import { useCallback, useEffect, useState } from 'react';
import Loading from '../../components/Loading';
import { classBugReports, deleteBugReport, replyToBug, setBugStatus, type BugReport } from '../../lib/bugReports';
import { useClassroom } from '../../state/Classroom';

// ─────────────────────────────────────────────────────────────────────────────
// The bug reports students sent from the 🐞 tab (components/BugReport.tsx):
// who, when, which page, what they said, and the technical details that came
// with it. Mark one fixed (the student sees ✅) or delete it.
// ─────────────────────────────────────────────────────────────────────────────

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** A page path in plain words. */
function pageName(path: string): string {
  const p = path.split('?')[0];
  const names: Record<string, string> = {
    '/': 'Home', '/video': 'Video', '/story': 'Story', '/grammar': 'Grammar', '/cards': 'Flashcards', '/vocab': 'Vocabulary',
    '/ocean': 'Ocean', '/shop': 'Shop', '/play': 'Class game', '/ocean/explore': 'Ocean (exploring)', '/ocean/arcade': 'Arcade', '/ocean/fortress': 'Sunken Fortress', '/ocean/make': 'Make a game', '/ocean/class-game': 'Class game', '/ocean/snake': 'Sea Snake', '/work': 'All work', '/account': 'Account', '/classes': 'My classes',
  };
  if (names[p]) return names[p];
  if (p.startsWith('/a/')) return 'An assignment';
  return p || 'Unknown page';
}

export default function BugReports() {
  const { active, students } = useClassroom();
  const [reports, setReports] = useState<BugReport[] | null>(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [openDetails, setOpenDetails] = useState<string | null>(null);
  const [replying, setReplying] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    if (!active) return;
    try { setReports(await classBugReports(active.id)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load the reports.'); }
  }, [active]);
  useEffect(() => { void load(); }, [load]);

  const act = async (f: () => Promise<void>) => { try { await f(); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'That didn’t work.'); } };

  if (!active) return <div className="notice">Pick a class first.</div>;
  if (error && !reports) return <div className="notice bad">{error}</div>;
  if (!reports) return <Loading what="bug reports" />;

  const name = (id: string) => students.find(s => s.user_id === id)?.display_name ?? 'A student';
  const list = reports.filter(r => show === 'all' || r.status === 'open');
  const openCount = reports.filter(r => r.status === 'open').length;

  return (
    <div className="bug-reports">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>🐞 Bug reports</h1>
        <div className="seg" role="radiogroup" aria-label="Show" style={{ margin: 0 }}>
          <button type="button" role="radio" aria-checked={show === 'open'} className={show === 'open' ? 'on' : ''} onClick={() => setShow('open')}>Open ({openCount})</button>
          <button type="button" role="radio" aria-checked={show === 'all'} className={show === 'all' ? 'on' : ''} onClick={() => setShow('all')}>All ({reports.length})</button>
        </div>
      </div>
      <p className="muted">What students reported from the 🐞 tab on their pages. Mark one fixed and the student sees ✅ next to it.</p>
      {error && <div className="notice bad">{error}</div>}
      {list.length === 0 ? (
        <div className="notice">{show === 'open' ? 'No open bug reports. 🎉' : 'No bug reports yet.'}</div>
      ) : list.map(r => {
        const d = r.details as { browser?: string; screen?: string; touch?: boolean; errors?: { at: string; message: string }[] };
        return (
          <div key={r.id} className={`panel bug-report ${r.status}`}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <strong>{name(r.student_id)}</strong> <span className="small muted">· {when(r.created_at)} · on <strong>{pageName(r.page)}</strong></span>
              </div>
              <div className="row" style={{ gap: 6 }}>
                {r.status === 'open'
                  ? <button type="button" className="small primary" onClick={() => void act(() => setBugStatus(r.id, 'fixed'))}>✅ Mark fixed</button>
                  : <button type="button" className="small" onClick={() => void act(() => setBugStatus(r.id, 'open'))}>Reopen</button>}
                <button type="button" className="small" onClick={() => { if (confirm('Delete this report?')) void act(() => deleteBugReport(r.id)); }}>Delete</button>
              </div>
            </div>
            <p className="bug-message" dir="auto">{r.message}</p>
            {replying === r.id ? (
              <div className="bug-reply-box">
                <textarea rows={3} maxLength={2000} value={draft} onChange={e => setDraft(e.target.value)} autoFocus dir="auto"
                  placeholder="Your answer: the student sees it in their 🐞 window." />
                <div className="row" style={{ gap: 6 }}>
                  <button type="button" className="small primary" disabled={!draft.trim()} onClick={() => void act(async () => { await replyToBug(r.id, draft); setReplying(null); })}>Send reply</button>
                  {r.status === 'open' && <button type="button" className="small" disabled={!draft.trim()} onClick={() => void act(async () => { await replyToBug(r.id, draft); await setBugStatus(r.id, 'fixed'); setReplying(null); })}>Send and mark fixed</button>}
                  <button type="button" className="small" onClick={() => setReplying(null)}>Cancel</button>
                </div>
              </div>
            ) : r.reply ? (
              <div className="bug-reply"><strong>Your reply</strong>{r.replied_at && <span className="small muted"> · {when(r.replied_at)}</span>}: <span dir="auto">{r.reply}</span>
                {' '}<button type="button" className="linkish small" onClick={() => { setReplying(r.id); setDraft(r.reply ?? ''); }}>Edit</button></div>
            ) : (
              <button type="button" className="small" onClick={() => { setReplying(r.id); setDraft(''); }}>💬 Reply</button>
            )}
            <button type="button" className="linkish small" onClick={() => setOpenDetails(o => (o === r.id ? null : r.id))}>
              {openDetails === r.id ? 'Hide technical details' : 'Technical details'}
            </button>
            {openDetails === r.id && (
              <div className="bug-details small">
                <div><strong>Page:</strong> {r.page || '—'}</div>
                <div><strong>Screen:</strong> {d.screen ?? '—'}{d.touch ? ' (touch screen)' : ''}</div>
                <div><strong>Browser:</strong> {d.browser ?? '—'}</div>
                <div><strong>Recent errors on the page:</strong> {d.errors?.length ? '' : 'none'}</div>
                {d.errors?.length ? <ul>{d.errors.map((e, k) => <li key={k}><code>{e.message}</code></li>)}</ul> : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
